// MyPlaces - User's Own Places Management
// Shows all places created by the user with status management

import React, { useState, useEffect } from 'react';
import {
  MapPin,
  Plus,
  Lock,
  Globe,
  Clock,
  Check,
  Share2,
  ChevronLeft,
  Loader2,
  Image as ImageIcon,
  ExternalLink,
  Users,
} from 'lucide-react';
import type { Place } from '../../types';
import { MAIN_CATEGORIES } from '../../types';
import { getUserPlaces, getSharedPlaces, makePlacePublic } from '../../services/placesService';

interface MyPlacesProps {
  userId: string;
  onBack: () => void;
  onViewPlace: (placeId: string) => void;
  onCreate: () => void;
  onSharePlace: (place: Place) => void;
  showNotification?: (message: string, type: 'success' | 'info' | 'error') => void;
}

type TabType = 'my' | 'shared';

export function MyPlaces({
  userId,
  onBack,
  onViewPlace,
  onCreate,
  onSharePlace,
  showNotification,
}: MyPlacesProps) {
  const [activeTab, setActiveTab] = useState<TabType>('my');
  const [myPlaces, setMyPlaces] = useState<Place[]>([]);
  const [sharedPlaces, setSharedPlaces] = useState<Place[]>([]);
  const [loading, setLoading] = useState(true);
  const [makingPublic, setMakingPublic] = useState<string | null>(null);

  // Load places
  useEffect(() => {
    const loadPlaces = async () => {
      setLoading(true);
      try {
        const [userPlaces, sharedWithMe] = await Promise.all([
          getUserPlaces(userId),
          getSharedPlaces(userId),
        ]);
        setMyPlaces(userPlaces);
        setSharedPlaces(sharedWithMe);
      } catch (error) {
        console.error('Error loading places:', error);
        showNotification?.('Fehler beim Laden der Orte', 'error');
      } finally {
        setLoading(false);
      }
    };

    loadPlaces();
  }, [userId, showNotification]);

  // Make place public
  const handleMakePublic = async (place: Place) => {
    if (makingPublic) return;

    setMakingPublic(place.id);
    try {
      await makePlacePublic(place.id, userId);
      // Update local state
      setMyPlaces((prev) =>
        prev.map((p) =>
          p.id === place.id
            ? { ...p, visibility: 'public', status: 'pending' }
            : p
        )
      );
      showNotification?.('Ort wird jetzt moderiert', 'success');
    } catch (error) {
      console.error('Error making place public:', error);
      showNotification?.(
        error instanceof Error ? error.message : 'Fehler beim Veroeffentlichen',
        'error'
      );
    } finally {
      setMakingPublic(null);
    }
  };

  // Get status badge for a place
  const getStatusBadge = (place: Place) => {
    // Private places
    if (place.visibility === 'private') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-100 text-indigo-700 text-xs font-medium rounded-full">
          <Lock className="w-3 h-3" />
          Privat
        </span>
      );
    }

    // Public places by status
    switch (place.status) {
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
            <Clock className="w-3 h-3" />
            Ausstehend
          </span>
        );
      case 'approved':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
            <Check className="w-3 h-3" />
            Freigegeben
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
            Abgelehnt
          </span>
        );
      default:
        return null;
    }
  };

  // Get category label
  const getCategoryLabel = (categoryId: string): string => {
    const category = MAIN_CATEGORIES.find((c) => c.id === categoryId);
    return category?.label || categoryId;
  };

  const currentPlaces = activeTab === 'my' ? myPlaces : sharedPlaces;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-full transition"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h1 className="font-semibold text-gray-900">Meine Orte</h1>
            <button
              onClick={onCreate}
              className="p-2 bg-indigo-500 text-white rounded-full hover:bg-indigo-600 transition"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab('my')}
              className={`flex-1 py-3 text-sm font-medium border-b-2 transition ${
                activeTab === 'my'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Meine Orte ({myPlaces.length})
            </button>
            <button
              onClick={() => setActiveTab('shared')}
              className={`flex-1 py-3 text-sm font-medium border-b-2 transition ${
                activeTab === 'shared'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Mit mir geteilt ({sharedPlaces.length})
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
          </div>
        ) : currentPlaces.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              {activeTab === 'my' ? (
                <MapPin className="w-8 h-8 text-gray-400" />
              ) : (
                <Users className="w-8 h-8 text-gray-400" />
              )}
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {activeTab === 'my'
                ? 'Noch keine eigenen Orte'
                : 'Noch keine geteilten Orte'}
            </h3>
            <p className="text-gray-500 mb-6">
              {activeTab === 'my'
                ? 'Erstelle deinen ersten GoodPlace und teile ihn mit der Community.'
                : 'Hier erscheinen Orte, die andere mit dir teilen.'}
            </p>
            {activeTab === 'my' && (
              <button
                onClick={onCreate}
                className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-500 text-white font-semibold rounded-lg hover:bg-indigo-600 transition"
              >
                <Plus className="w-5 h-5" />
                Ort erstellen
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {currentPlaces.map((place) => (
              <div
                key={place.id}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden"
              >
                <div className="flex gap-4 p-4">
                  {/* Image */}
                  <div
                    className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100 cursor-pointer"
                    onClick={() => onViewPlace(place.id)}
                  >
                    {place.imageUrl || place.images?.[0] ? (
                      <img
                        src={place.images?.[0] || place.imageUrl}
                        alt={place.name}
                        className="w-full h-full object-cover hover:scale-105 transition-transform"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ImageIcon className="w-8 h-8 text-gray-300" />
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <button
                          onClick={() => onViewPlace(place.id)}
                          className="font-semibold text-gray-900 hover:text-indigo-600 transition text-left"
                        >
                          {place.name}
                        </button>
                        <p className="text-sm text-gray-500 mt-0.5">
                          {getCategoryLabel(place.category)}
                        </p>
                      </div>
                      {getStatusBadge(place)}
                    </div>

                    {/* Location */}
                    {(place.address || place.city) && (
                      <p className="text-sm text-gray-500 mt-2 flex items-center gap-1">
                        <MapPin className="w-3.5 h-3.5" />
                        {place.address ? `${place.address}, ${place.city}` : place.city}
                      </p>
                    )}

                    {/* Shared with count */}
                    {activeTab === 'my' &&
                      place.visibility === 'private' &&
                      place.sharedWith &&
                      place.sharedWith.length > 0 && (
                        <p className="text-sm text-indigo-600 mt-1 flex items-center gap-1">
                          <Users className="w-3.5 h-3.5" />
                          Mit {place.sharedWith.length} Person
                          {place.sharedWith.length > 1 ? 'en' : ''} geteilt
                        </p>
                      )}
                  </div>
                </div>

                {/* Action Buttons - Only for own places */}
                {activeTab === 'my' && (
                  <div className="flex border-t border-gray-100">
                    {/* View Button */}
                    <button
                      onClick={() => onViewPlace(place.id)}
                      className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium text-gray-600 hover:bg-gray-50 transition"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Ansehen
                    </button>

                    {/* Share Button - Only for private places */}
                    {place.visibility === 'private' && (
                      <>
                        <div className="w-px bg-gray-100" />
                        <button
                          onClick={() => onSharePlace(place)}
                          className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium text-indigo-600 hover:bg-indigo-50 transition"
                        >
                          <Share2 className="w-4 h-4" />
                          Teilen
                        </button>
                      </>
                    )}

                    {/* Make Public Button - Only for private places */}
                    {place.visibility === 'private' && (
                      <>
                        <div className="w-px bg-gray-100" />
                        <button
                          onClick={() => handleMakePublic(place)}
                          disabled={makingPublic === place.id}
                          className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium text-green-600 hover:bg-green-50 transition disabled:opacity-50"
                        >
                          {makingPublic === place.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Globe className="w-4 h-4" />
                          )}
                          Oeffentlich
                        </button>
                      </>
                    )}

                    {/* Status for public places */}
                    {place.visibility === 'public' && place.status === 'pending' && (
                      <>
                        <div className="w-px bg-gray-100" />
                        <div className="flex-1 flex items-center justify-center gap-2 py-3 text-sm text-amber-600">
                          <Clock className="w-4 h-4" />
                          Wird geprueft
                        </div>
                      </>
                    )}

                    {place.visibility === 'public' && place.status === 'approved' && (
                      <>
                        <div className="w-px bg-gray-100" />
                        <div className="flex-1 flex items-center justify-center gap-2 py-3 text-sm text-green-600">
                          <Globe className="w-4 h-4" />
                          Live
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Action for shared places - just view */}
                {activeTab === 'shared' && (
                  <div className="flex border-t border-gray-100">
                    <button
                      onClick={() => onViewPlace(place.id)}
                      className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium text-gray-600 hover:bg-gray-50 transition"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Ansehen
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default MyPlaces;
